import React from 'react';
import { FreeConsultLink } from './FreeConsultLink';
import { XCircleIcon, LightbulbIcon } from 'lucide-react';
export const ProblemDeepening = ({
  openModal
}) => {
  return <section className="mb-10" data-id="element-421">
      <h2 className="text-xl font-bold text-blue-800 mb-4" data-id="element-422">
        "Gewoon Oefenen" maakt het probleem erger
      </h2>
      <div className="bg-red-50 p-5 rounded-lg mb-8 border-l-4 border-red-500" data-id="element-423">
        <h3 className="text-lg font-bold text-red-800 flex items-center mb-4" data-id="element-424">
          <XCircleIcon className="w-6 h-6 mr-2 text-red-600" data-id="element-425" />
          Waarom de traditionele aanpak niet werkt
        </h3>
        <p className="mb-4 leading-relaxed" data-id="element-426">
          Stel je voor dat je doodsbang bent voor vogels en daarom niet meer
          naar buiten durft. Nu zegt iemand tegen je: "We gaan oefenen met jouw
          angst om naar buiten te gaan! Elke dag ga je een minuutje langer naar
          buiten tot je angst verdwijnt."
        </p>
        <p className="mb-4 leading-relaxed" data-id="element-427">
          En misschien gaat het soms zelfs goed - je staat buiten, voelt je
          ontspannen, denkt dat je vooruitgang boekt.
        </p>
        <p className="mb-4 leading-relaxed" data-id="element-428">Totdat je weer een vogel ziet.</p>
        <p className="mb-4 leading-relaxed" data-id="element-429">
          Dan schiet je weer in paniek en ben je terug bij af.
        </p>
        <p className="mb-4 leading-relaxed" data-id="element-430">
          Klinkt logisch om te blijven oefenen, toch?
        </p>
        <p className="mb-4 leading-relaxed font-semibold" data-id="element-431">Verkeerd.</p>
        <p className="mb-4 leading-relaxed" data-id="element-432">
          Je moet eerst die angst voor vogels oplossen voordat je succesvol naar
          buiten kunt. Anders blijf je fragiel - bij elke vogel die je ziet
          stort je kaartenhuis in elkaar en krijg je weer een bevestiging dat
          het buiten eng is.
        </p>
        <p className="mb-4 leading-relaxed" data-id="element-433">
          Hetzelfde geldt voor honden met "verlatingsangst." Een veelvoorkomende
          onderliggende oorzaak is bijvoorbeeld angst voor harde geluiden.
          Zolang je hond bang blijft voor sirenes, dichtslaan van deuren, of
          verkeer, kun je zo veel oefenen als je wilt met alleen laten - zodra
          er een hard geluid klinkt terwijl jij weg bent, is alle vooruitgang
          weg.
        </p>
        <p className="mb-4 leading-relaxed" data-id="element-434">
          Als je blijft oefenen zonder de echte oorzaak aan te pakken, dan:
        </p>
        <ul className="mb-4 list-disc pl-6 space-y-2" data-id="element-435">
          <li data-id="element-436">
            Overvraag je je hond constant - Je duwt hem keer op keer over zijn
            comfortzone
          </li>
          <li data-id="element-437">
            Versterk je de onderliggende angst - Elke "oefensessie" bevestigt
            dat weggaan eng is
          </li>
          <li data-id="element-438">
            Creëer je een vicieuze cirkel - Meer stress bij vertrek → angstiger
            gedrag → meer oefening → meer stress
          </li>
          <li data-id="element-439">
            Blijft je hond fragiel - Een klein geluid, een kleine verandering en
            hij valt terug in oude patronen
          </li>
        </ul>
        <p className="mb-4 leading-relaxed" data-id="element-440">
          Geen wonder dat traditionele training maanden duurt, vaak niet werkt,
          en bijna altijd resulteert in terugval.
          <br data-id="element-441" />
          <br data-id="element-442" />
          <a href="https://training.stresslessdogs.nl/va-video" className="text-blue-600 hover:underline" data-id="element-443">
            En daarom bieden wij behandel trajecten aan die wel de kern
            aanpakken
          </a>
        </p>
      </div>
      <div className="w-full mb-6" data-id="element-444">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/mtTrhFKeHXYZb6xQYbVNmu/freepik__the-style-is-candid-image-photography-with-natural__77409.png" alt="Bezorgde vrouw staat bij het raam terwijl haar hond verdrietig op de bank ligt" className="w-full h-auto rounded-lg shadow-md" data-id="element-445" />
        <p className="text-xs text-gray-500 mt-1 text-center italic" data-id="element-446">
          "Het probleem dat je probeert op te lossen, is vaak niet het echte
          probleem"
        </p>
      </div>
      <div className="bg-blue-50 p-5 rounded-lg mb-8 border-l-4 border-blue-500" data-id="element-447">
        <h3 className="text-lg font-bold text-blue-800 flex items-center mb-4" data-id="element-448">
          <LightbulbIcon className="w-6 h-6 mr-2 text-blue-600" data-id="element-449" />
          Verborgen oorzaken, een aantal voorbeelden
        </h3>
        <p className="mb-4 leading-relaxed" data-id="element-450">
          Tijdens{' '}
          <a href="https://training.stresslessdogs.nl/va-video" className="text-blue-600 hover:underline" data-id="element-451">
            verlatingsangst trajecten
          </a>{' '}
          zien we dezelfde verhalen keer op keer. Baasjes die maanden hebben
          geoefend zonder resultaat. Honden die steeds angstiger worden. Baasjes
          die niet zonder oppas weg van huis kunnen.
        </p>
        <p className="mb-4 leading-relaxed" data-id="element-452">
          En dan doe ik met mijn team een grondige diagnose en ontdek ik de
          echte oorzaak:
        </p>
        <h4 className="font-bold mb-2" data-id="element-453">Sarah en Rocky - Verveling</h4>
        <p className="mb-4 leading-relaxed" data-id="element-454">
          Sarah dacht dat Rocky verlatingsangst had. Hij vernielde het hele huis
          zodra ze wegging. Maanden van oefenen hielp niet.
        </p>
        <p className="mb-4 leading-relaxed" data-id="element-455">
          De echte oorzaak? Rocky was een Border Collie die mentaal
          onderprikkeld was. Hij was niet bang om verlaten te worden - hij was
          ontzettend verveeld en gefrustreerd. Toen we hem meer mentale
          uitdaging gaven, ging het oefenen veel makkelijker en kon hij twee
          maanden later weer als een normale hond alleen thuis blijven.
        </p>
        <h4 className="font-bold mb-2" data-id="element-456">Tom en Bella - Geluidsangst</h4>
        <p className="mb-4 leading-relaxed" data-id="element-457">
          Tom had een jaar lang geoefend met Bella. Vijf minuten weg, tien
          minuten, steeds iets langer. Bella werd alleen maar hystérischer.
        </p>
        <p className="mb-4 leading-relaxed" data-id="element-458">
          Waarom? Bella had een extreme geluidsangst en zonder hhaar baasje in
          de buurt was dit erger. Alleen thuis betekende daarom confrontatie met
          enge geluiden van de buren, verkeer, sirenes. Ze was niet bang voor
          Tom's vertrek - ze was bang voor wat er zou gebeuren als ze alleen
          moest omgaan met die geluiden. Daarom kreeg ze al stress als ze alleen
          was.
        </p>
        <p className="mb-4 leading-relaxed" data-id="element-459">
          Nadat we de geluidsangst hadden behandeld moest er nog wel geoefend
          worden. Maar dit keer zonder terugval en met grote stappen.
        </p>
        <h4 className="font-bold mb-2" data-id="element-460">Lisa en Max - FOMO</h4>
        <p className="mb-4 leading-relaxed" data-id="element-461">
          Max kon letterlijk geen seconde alleen blijven. Lisa dacht dat hij
          doodsangst had om verlaten te worden.
        </p>
        <p className="mb-4 leading-relaxed" data-id="element-462">
          De realiteit? Max had Fear of Missing Out. Hij wilde gewoon overal bij
          zijn, niet omdat hij bang was, maar omdat het met zijn baasje altijd
          veel leuker was dan zonder zijn baasjes. Hij moest dus alleen thuis
          blijven ook heel leuk gaan vinden.
        </p>
      </div>
      <h3 className="text-lg font-bold text-blue-800 mb-3" data-id="element-463">Vicieuze cirkel</h3>
      <p className="mb-6 leading-relaxed" data-id="element-464">
        Waarom lijkt het op verlatingsangst? Omdat de angsten versterken als je
        weg bent. Je hond weet dit en krijgt al stress als je de sleutels pakt.
        Dit creëert een vicieuze cirkel: stress bij vertrek → meer oefenen →
        bevestiging van zijn angst → meer oefenen
      </p>
      <div className="bg-gray-50 p-5 rounded-lg mb-6" data-id="element-465">
        <p className="italic text-gray-700" data-id="element-466">
          "Na twee jaar worstelen met 'verlatingsangst' ontdekten we dat onze
          Max eigenlijk geluidsangst had. Hij was doodsbang voor de vrachtwagens
          die langskwamen als wij weg waren. Zodra we dát probleem aanpakten,
          kon hij binnen een maand alleen blijven." - Emma uit Amsterdam
        </p>
      </div>
      <p className="mb-6 leading-relaxed font-semibold" data-id="element-467">
        Bij de meeste van onze cliënten had de hond geen verlatingsangst, maar
        één van deze onderliggende problemen. Zodra we het echte probleem
        aanpakten, ging het oefenen erg snel.
      </p>
      <p className="mb-6 leading-relaxed" data-id="element-468">
        <FreeConsultLink onClick={openModal} data-id="element-469">
          Wil je weten wat er écht speelt bij jouw hond?
        </FreeConsultLink>{' '}
        Bekijk dan of verlatingsangst coaching bij je past en plan een
        vrijblijvend intake gesprek.
      </p>
    </section>;
};